import { $LegsClassico, $LegsWithOffset, $LegsWithThickness, $SteelLegs, $TopCircle, $TopRectangle } from '../__generated/enhanced-model.js';
import { implementCustomExpressions, implementCustomInvariants } from '../__generated/implementation.js';
import { LegsThickness, TopMaterial } from '../__generated/model/index.js';
import { LEGS_LABEL_MAP } from '../data/labels.js';
import { OakLegProperties, SteelLegProperties, Textures } from '../data/materials.js';
import { LEG_THICKNESS_OPTIONS, LEG_THICKNESS_OPTION_MAP } from '../data/options.js';
import { LEGS_COLOR_STEEL_PRICE_MAP, LEGS_PRICE_MAP, LEGS_WITH_THICKNESS_PRICE_MAP } from '../data/prices.js';
import { ColorOakSwatchMap, SteelLegsSwatchMap } from '../data/swatches.js';
const AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX = [
    TopMaterial.SolidOak,
    TopMaterial.Ceramic,
    TopMaterial.Fenix
];
const AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC = [
    TopMaterial.SolidOak,
    TopMaterial.Ceramic
];
const AVAILABLE_MATERIALS_SOLID_OAK = [
    TopMaterial.SolidOak
];
const AVAILABLE_LEG_OFFSETS = [
    200,
    300,
    400,
    500
];
const LEG_OFFSET_OPTIONS = AVAILABLE_LEG_OFFSETS.map((offset)=>({
        value: offset,
        label: `${offset / 10} cm`
    }));
export const legsExpressions = implementCustomExpressions({
    Legs: {
        $descriptionLegsColor: (self)=>$SteelLegs.is(self) ? SteelLegsSwatchMap[self.color].swatchName : ColorOakSwatchMap[self.color].swatchName,
        $descriptionLegOffset: (self)=>$LegsWithOffset.is(self) ? {
                id: "f479pP",
                message: "Offset {0}cm",
                values: {
                    0: self.legOffset / 10
                }
            } : '',
        $descriptionLegStyle: (self)=>LEGS_LABEL_MAP[self.type],
        $descriptionLegThickness: (self)=>{
            if ($LegsWithThickness.is(self)) {
                return LEG_THICKNESS_OPTION_MAP[self.legThickness].label;
            }
            return '';
        },
        $height: (self)=>self.$table.height - self.$table.$top.thickness,
        $offsetY: (self)=>-self.$table.height / 2,
        $price: (self)=>{
            const legStylePrice = $LegsWithThickness.is(self) ? LEGS_WITH_THICKNESS_PRICE_MAP[self.type][self.legThickness] : LEGS_PRICE_MAP[self.type];
            // Assumption: Oak legs are the same colour as the table top, so any extra price is already counted.
            const legColorPrice = $SteelLegs.is(self) ? LEGS_COLOR_STEEL_PRICE_MAP[self.color] : 0;
            return legStylePrice + legColorPrice;
        }
    },
    Legs3D: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $length: (self)=>0.7 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.$morphLength),
        $width: (self)=>0.7 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width)
    },
    Legs3DRonde: {
        $objectName: (self)=>self.legThickness === LegsThickness.Thick ? '3D_POOT_60x60_SUPPORT' : '3D_POOT_50x30_SUPPORT',
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $diameter: (self)=>$TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width
    },
    LegsAxel: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $length: (self)=>0.75 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.$morphLength),
        $width: (self)=>0.75 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width)
    },
    LegsBrago: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC,
        $width: (self)=>{
            if ($TopCircle.is(self.$table.$top)) {
                return self.$table.$top.diameter / 2;
            }
            return 0.55 * self.$table.$top.width;
        }
    },
    LegsButterfly: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $width: (self)=>{
            if ($TopCircle.is(self.$table.$top)) {
                return self.$table.$top.diameter / 2;
            }
            if ($TopRectangle.is(self.$table.$top)) {
                return 0.75 * self.$table.$top.width;
            }
            // other shapes need to be narrower so the support fits underneath
            return 0.75 * self.$table.$top.width - (400 - self.legOffset);
        }
    },
    LegsClassico: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK,
        $calculatedWidth: (self)=>{
            if ($TopCircle.is(self.$table.$top)) {
                return self.$table.$top.diameter / 2;
            }
            return 0.7 * self.$table.$top.width;
        },
        $width: (self)=>Math.min(self.$calculatedWidth, 800)
    },
    LegsCono: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC,
        $diameter: (self)=>$TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width
    },
    LegsDiamond: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $length: (self)=>0.6 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : Math.min(self.$table.$top.length, 2200)),
        $width: (self)=>0.4 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width)
    },
    LegsEbonu: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $length: (self)=>0.75 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.$morphLength),
        $width: (self)=>0.75 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width)
    },
    LegsEbonu3: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $diameter: (self)=>0.5 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width),
        $offsetZ: (self)=>0.07 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width)
    },
    LegsEbonu3Double: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX
    },
    LegsEbu: {
        $objectName: (self)=>$TopCircle.is(self.$table.$top) || self.$table.$top.$morphLength <= 2400 ? 'EBU_SUPPORT_3x3' : 'EBU_SUPPORT_3x5',
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $width: (self)=>0.85 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width)
    },
    LegsElmo: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC,
        $diameter: (self)=>$TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width
    },
    LegsElmoDouble: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC
    },
    LegsFalke: {
        $objectName: (self)=>{
            const size = $TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.$morphLength;
            const isSmall = self.$table.$top.material === TopMaterial.SolidOak && size <= 2400;
            return isSmall ? 'FALCON_LEG_60X20' : 'FALCON_LEG_80X40';
        },
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $size: (self)=>0.5 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.$morphLength)
    },
    LegsFly: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $length: (self)=>0.75 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.$morphLength),
        $width: (self)=>0.75 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width)
    },
    LegsHarper: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $length: (self)=>0.6 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.$morphLength),
        $width: (self)=>$TopCircle.is(self.$table.$top) ? 0.6 * self.$table.$top.diameter : 0.8 * self.$table.$top.width
    },
    LegsHarper3: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $diameter: (self)=>$TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width
    },
    LegsJulio3: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $diameter: (self)=>$TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width
    },
    LegsJulio4: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $diameter: (self)=>$TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width
    },
    LegsLivio: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC,
        $length: (self)=>0.5 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.$morphLength),
        $width: ()=>400
    },
    LegsLivioSchuin: {
        $length: (self)=>0.5 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.$morphLength),
        $width: ()=>400
    },
    LegsLivioSchuinEik: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC
    },
    LegsLivioSchuinStaal: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX
    },
    LegsMadrid: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $diameter: (self)=>$TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width
    },
    LegsMPootRecht: {
        $objectName: (self)=>self.legThickness === LegsThickness.Thick ? 'M_POOT_RECHT_SUPPORT_80x80' : 'M_POOT_RECHT_SUPPORT_80x40',
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $length: (self)=>0.8 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.$morphLength),
        $width: (self)=>0.8 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width)
    },
    LegsMPootSchuin: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $length: (self)=>0.8 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.$morphLength),
        $width: (self)=>0.8 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width)
    },
    LegsNewly: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $diameter: (self)=>$TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width
    },
    LegsNovaroEik: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC
    },
    LegsNovaroStaal: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX
    },
    LegsNovaroDoubleEik: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC
    },
    LegsNovaroDoubleStaal: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX
    },
    LegsOdil3: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $diameter: (self)=>$TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width
    },
    LegsOdil4: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $diameter: (self)=>$TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width
    },
    LegsOslo: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC,
        $diameter: (self)=>$TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width
    },
    LegsOxford: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX
    },
    LegsPapillon: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC,
        $width: ()=>600
    },
    LegsPapillonStrak: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC,
        $width: ()=>500
    },
    LegsPino: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK,
        $diameter: (self)=>$TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width
    },
    LegsRafino: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $length: (self)=>0.7 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.$morphLength),
        $width: (self)=>0.8 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width)
    },
    LegsRobu: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $diameter: (self)=>$TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width
    },
    LegsRobuDouble: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX
    },
    LegsRoia: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK,
        $diameter: (self)=>$TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width
    },
    LegsTPoot: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $length: (self)=>0.75 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.$morphLength),
        $width: (self)=>0.75 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width)
    },
    LegsTPootRonde: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $length: (self)=>0.75 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.$morphLength),
        $width: (self)=>0.75 * ($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width)
    },
    LegsTrapezium: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $width: (self)=>{
            if ($TopCircle.is(self.$table.$top)) {
                return self.$table.$top.diameter / 2;
            }
            if ($TopRectangle.is(self.$table.$top)) {
                return 0.9 * self.$table.$top.width;
            }
            // other shapes need to be narrower so the support fits underneath
            return self.$table.$top.width / 2 + self.legOffset;
        }
    },
    LegsTwist3: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $diameter: (self)=>$TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.width,
        $objectName: (self)=>self.legThickness === LegsThickness.Thick ? 'TWIST_3_ROND_SUPPORT' : 'TWIST_3_ROND_THIN_SUPPORT'
    },
    LegsTwist4: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $length: ()=>1500,
        $objectName: (self)=>self.legThickness === LegsThickness.Thick ? 'TWIST_4_SUPPORT' : 'TWIST_4_THIN_SUPPORT',
        $width: ()=>800
    },
    LegsUPoot: {
        $objectName: (self)=>self.legThickness === LegsThickness.Thick ? 'U_POOT_SUPPORT_80x40' : 'U_POOT_SUPPORT_80x20',
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $width: (self)=>{
            if ($TopCircle.is(self.$table.$top)) {
                return self.$table.$top.diameter / 2;
            }
            if ($TopRectangle.is(self.$table.$top)) {
                return 0.9 * self.$table.$top.width;
            }
            // other shapes need to be narrower so the support fits underneath
            return self.$table.$top.width / 2 - (200 - self.legOffset);
        }
    },
    LegsXPoot: {
        $availableMaterials: ()=>AVAILABLE_MATERIALS_SOLID_OAK_CERAMIC_FENIX,
        $width: (self)=>{
            if ($TopCircle.is(self.$table.$top)) {
                return self.$table.$top.diameter / 2;
            }
            if ($TopRectangle.is(self.$table.$top)) {
                return 0.8 * self.$table.$top.width;
            }
            // other shapes need to be narrower so the support fits underneath
            return self.$table.$top.width / 2 - (200 - self.legOffset);
        }
    },
    LegsWithOffset: {
        $legOffsetOptions: ()=>LEG_OFFSET_OPTIONS,
        $length: (self)=>($TopCircle.is(self.$table.$top) ? self.$table.$top.diameter : self.$table.$top.length) - self.legOffset * 2
    },
    LegsWithThickness: {
        $legThicknessOptions: ()=>LEG_THICKNESS_OPTIONS
    },
    SteelLegs: {
        $legsColorSwatches: ()=>SteelLegsSwatchMap,
        $materialColor: (s)=>SteelLegProperties[s.color].color,
        $normalUrl: ()=>Textures.STEEL_NORMAL_URL,
        $roughnessMapUrl: ()=>Textures.STEEL_ROUGHNESS_URL
    },
    OakLegs: {
        $legsColorSwatches: ()=>ColorOakSwatchMap,
        $materialColor: (s)=>OakLegProperties[s.color].color,
        $textureUrl: ()=>Textures.TOP_OAK_BASE_COLOR_URL,
        $normalMapUrl: ()=>Textures.TOP_OAK_NORMAL_URL,
        $roughnessMapUrl: ()=>Textures.TOP_LIVED_OAK_ROUGHNESS_URL
    }
});
export const legsInvariants = implementCustomInvariants({
    Legs: {
        oakLegsAndTopColorNotMatching: (self)=>{
            if ($SteelLegs.is(self)) {
                return true;
            }
            if (self.$table.$top.material !== TopMaterial.SolidOak) {
                return true;
            }
            return self.$table.$top.color === self.color;
        },
        tooCloseToEndOfTable: (self)=>{
            if ($LegsClassico.is(self)) {
                if (self.$calculatedWidth >= 800) {
                    return true;
                }
                return self.legOffset === 500;
            }
            return true;
        },
        topMaterialNotSupported: (self)=>{
            return self.$availableMaterials.includes(self.$table.$top.material);
        },
        topShapeNotSupported: (self)=>{
            return self.$table.$top.$availableLegTypes.includes(self.type);
        }
    }
});

//# sourceMappingURL=legs.js.map